import { useEffect, useState } from "react";
import { DEFAULT_OBS_CONFIG, normalizeObsConfig, useObs } from "../hooks/useObs";
import { MonitorPlay, Save, Link2, Link2Off, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const OBS_LOCAL_CONFIG_KEY = "thebarracksbunny.obs.localConfig";
const defaultObsConfig = DEFAULT_OBS_CONFIG;

function loadLocalObsConfig() {
  try {
    const parsed = JSON.parse(localStorage.getItem(OBS_LOCAL_CONFIG_KEY) || "{}");
    return normalizeObsConfig({ ...defaultObsConfig, ...parsed });
  } catch (err) {
    return defaultObsConfig;
  }
}

export default function OBS() {
  const [cfg, setCfg] = useState(defaultObsConfig);
  const [loaded, setLoaded] = useState(false);
  const { connected, scenes, currentScene, error, url, connect, disconnect, setScene } = useObs(cfg);
  const httpsPage = window.location.protocol === "https:";

  useEffect(() => {
    setCfg(loadLocalObsConfig());
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (!loaded || connected || localStorage.getItem("obsAutoConnect") !== "true") return;
    connect();
  }, [loaded, connected, connect]);

  const save = () => {
    const normalized = normalizeObsConfig(cfg);
    setCfg(normalized);
    localStorage.setItem(OBS_LOCAL_CONFIG_KEY, JSON.stringify(normalized));
    toast.success("OBS config saved on this browser");
  };

  const reset = () => {
    disconnect();
    localStorage.removeItem(OBS_LOCAL_CONFIG_KEY);
    setCfg(defaultObsConfig);
    toast.success("OBS config reset");
  };

  return (
    <div className="space-y-6" data-testid="obs-page">
      <div>
        <div className="label-eyebrow">Local OBS WebSocket v5</div>
        <h1 className="text-2xl font-semibold tracking-tight mt-1 flex items-center gap-2">
          <MonitorPlay className="w-5 h-5 text-[hsl(var(--primary))]" /> OBS Control
        </h1>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
          This page connects from this browser to OBS on this computer. The production backend cannot connect to OBS running on your streaming PC.
        </p>
      </div>

      {httpsPage && (
        <div className="rounded border border-yellow-500/40 bg-yellow-500/10 px-3 py-2 text-sm text-yellow-100">
          If your browser blocks local OBS WebSocket connections from the live HTTPS site, open the local development site for OBS control. The OBS connection is local to your streaming PC.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="surface p-5 space-y-3">
          <div className="label-eyebrow">Connection</div>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">
            In OBS: Tools, WebSocket Server Settings, enable WebSocket server. Default port is 4455.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Host" value={cfg.host} onChange={(v) => setCfg({ ...cfg, host: v })} test="obs-host" />
            <Field label="Port" type="number" value={cfg.port} onChange={(v) => setCfg({ ...cfg, port: parseInt(v || 0, 10) })} test="obs-port" />
          </div>
          <Field label="Password" value={cfg.password} type="password" onChange={(v) => setCfg({ ...cfg, password: v })} test="obs-password" />
          <div className="flex flex-wrap items-center gap-3 pt-2">
            <button onClick={save} className="text-xs uppercase tracking-widest border border-[hsl(var(--border))] px-3 py-2 rounded flex items-center gap-2" data-testid="save-obs-config">
              <Save className="w-3 h-3" /> Save local config
            </button>
            <button onClick={reset} className="text-xs uppercase tracking-widest border border-[hsl(var(--border))] px-3 py-2 rounded flex items-center gap-2" data-testid="reset-obs-config">
              <RotateCcw className="w-3 h-3" /> Reset
            </button>
            {!connected ? (
              <button onClick={connect} disabled={!loaded} className="text-xs uppercase tracking-widest bg-[hsl(var(--primary))] text-white px-3 py-2 rounded flex items-center gap-2" data-testid="obs-connect-btn">
                <Link2 className="w-3 h-3" /> Connect
              </button>
            ) : (
              <button onClick={disconnect} className="text-xs uppercase tracking-widest bg-[hsl(var(--destructive))] text-white px-3 py-2 rounded flex items-center gap-2" data-testid="obs-disconnect-btn">
                <Link2Off className="w-3 h-3" /> Disconnect
              </button>
            )}
            <span className="flex items-center gap-2 text-xs ml-auto">
              <span className={`status-dot ${connected ? "online" : "offline"}`} /> {connected ? "Connected" : "Disconnected"}
            </span>
          </div>
          {url && <div className="text-xs text-[hsl(var(--muted-foreground))]">Attempted: <code>{url}</code></div>}
          {error && <div className="text-xs text-[hsl(var(--destructive))]" data-testid="obs-error">{error}</div>}
        </div>

        <div className="surface p-5">
          <div className="label-eyebrow mb-3">Scenes</div>
          {!connected && <div className="text-sm text-[hsl(var(--muted-foreground))]">Connect to OBS to list scenes.</div>}
          {connected && scenes.length === 0 && <div className="text-sm text-[hsl(var(--muted-foreground))]">No scenes detected.</div>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {scenes.map((scene) => (
              <button
                key={scene}
                onClick={() => setScene(scene)}
                data-testid={`scene-${scene}`}
                className={`text-left px-3 py-2 rounded border text-sm font-mono ${scene === currentScene ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10 text-white" : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:border-[hsl(var(--primary))] hover:text-white"}`}
              >
                {scene === currentScene && "* "}{scene}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label = "", value, onChange, type = "text", test } = {}) {
  return (
    <div>
      <div className="label-eyebrow mb-1.5">{label}</div>
      <input type={type} value={value || ""} onChange={(event) => onChange(event.target.value)} data-testid={test} className="w-full bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] rounded px-3 py-2 text-sm font-mono" />
    </div>
  );
}
